import { useState, useRef, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import posthog from 'posthog-js'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ChartLine, Check, Code2, Download, IdCard, LayoutList, Loader2, Plus, Upload, ImageIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardHeader, CardIcon, CardTitle, CardContent, CardAction } from '@/components/ui/card'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
} from '../../components/StatsBar'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AdvancedSection } from '../../components/AdvancedSection'
import { ShareOfVoiceInput } from '../../components/ShareOfVoiceInput'
import { FieldLabel } from '../../components/FieldLabel'
import { TrackingUrlListInput } from '../../components/TrackingUrlListInput'
import { AppShell } from '../../components/AppShell'
import { AssetHoverPreview } from '../../components/AssetHoverPreview'
import { DEFAULT_HTML_SIZE, ScaledHtmlFrame } from '../../components/HtmlPreviewThumbnail'
import { CodeEditor } from '../../components/CodeEditor'
import { CreativePreviewCard } from '../../components/CreativePreviewCard'
import { CreativeTagsPanel } from '../../components/creatives/CreativeTagsPanel'
import { RouteTabs } from '../../components/RouteTabs'
import { useNetwork } from '../../contexts/NetworkContext'
import type { PreviewLayoutParameter } from '@/services/creatives/previewApi'
import { VariableReferenceSheet } from '../../components/VariableReferenceSheet'
import { GENERAL_VARIABLE_GROUP, THIRD_PARTY_VARIABLE_GROUPS } from '../../lib/adCodeVariables'
import { FormSkeleton } from '../../components/FormSkeleton'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { AuthService } from '@/services/core/auth'
import { CreativesApi, Creative, CreativeInput } from '@/services/creatives/creativesApi'
import { LayoutsApi, Layout, LayoutComponent } from '@/services/sites/layoutsApi'
import { AssetsApi, isHtmlAsset, isVideoAsset } from '@/services/assets/assetsApi'
import { targetingForSave, type Targeting } from '@/services/targeting/targeting'
import { TargetingCard } from '../../components/targeting/TargetingCard'
import { downloadAsset, downloadAssetsZip } from '@/services/assets/assetDownloads'
import { useObjectCrumbs } from '../../hooks/useObjectCrumbs'
import { ObjectAncestorsList } from '../../components/ObjectAncestorsList'
import { useLineItemAncestors } from '../../hooks/useLineItemAncestors'

// ── Asset components ──────────────────────────────────────────────────────────

import { Asset } from '@/services/assets/assetsApi'
import { formatDate, formatNumber } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { StatsPanel } from '@/components/StatsPanel'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { CreativeStateSelect } from '@/components/CreativeStateSelect'
import { CreativeRejectedAlert } from '@/components/CreativeRejectedAlert'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

interface AssetDropzoneProps {
  isUploading: boolean
  onUpload: (file: File) => void
  uploadLabel: string
  uploadingLabel: string
}

function AssetDropzone({ isUploading, onUpload, uploadLabel, uploadingLabel }: AssetDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        const file = e.dataTransfer.files?.[0]
        if (file) onUpload(file)
      }}
      onClick={() => !isUploading && fileInputRef.current?.click()}
      style={{
        border: `2px dashed ${dragging ? 'var(--ad-primary)' : 'var(--ad-border)'}`,
        borderRadius: 8,
        padding: '20px 16px',
        textAlign: 'center',
        cursor: isUploading ? 'default' : 'pointer',
        background: dragging ? 'color-mix(in srgb, var(--ad-primary) 6%, transparent)' : 'transparent',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {isUploading ? (
        <span className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {uploadingLabel}
        </span>
      ) : (
        <span className="flex flex-col items-center gap-1">
          <Upload className="size-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{uploadLabel}</span>
        </span>
      )}
      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        accept="image/*,video/*"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onUpload(file)
          e.target.value = ''
        }}
      />
    </div>
  )
}

interface AssetSlotProps {
  label: string
  assets: Asset[]
  selectedAssetId: string | null
  mimeTypes?: string[]
  onSelect: (assetId: string) => void
  uploadProgress?: number | null
  lastUploadedAsset?: Asset | null
  onDownloadAsset: (asset: Asset) => void
  downloadLabel: string
}

function AssetSlot({ label, assets, selectedAssetId, mimeTypes, onSelect, uploadProgress, lastUploadedAsset, onDownloadAsset, downloadLabel }: AssetSlotProps) {
  const showUpload = uploadProgress !== null && uploadProgress !== undefined
  const compatibleAssets = assets.filter((a) => !mimeTypes?.length || mimeTypes.includes(a.mimeType))
  const isEmpty = compatibleAssets.length === 0 && !showUpload

  return (
    <div className="p-field full">
      <label>{label}</label>
      {isEmpty ? (
        <p className="text-sm text-muted-foreground">{assets.length > 0 ? 'No compatible asset available' : '—'}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {compatibleAssets.map((asset) => {
            const isSelected = asset.id === selectedAssetId
            const isVideo = isVideoAsset(asset.mimeType ?? '')
            const src = isVideo ? asset.cdnId : (asset.previewCdnId || asset.cdnId)
            const thumbStyle = { maxHeight: 120, maxWidth: 180, borderRadius: 4, display: 'block', objectFit: 'contain' as const }
            return (
              <AssetHoverPreview
                key={asset.id}
                src={src}
                mimeType={asset.mimeType}
                caption={asset.fileName}
                fileSize={asset.fileSize}
                width={asset.width}
                height={asset.height}
                onDownload={() => onDownloadAsset(asset)}
                downloadLabel={downloadLabel}
              >
              <button
                type="button"
                onClick={() => onSelect(asset.id)}
                title={asset.fileName}
                aria-pressed={isSelected}
                className={`relative cursor-pointer rounded-lg border-[3px] p-1 leading-none transition-colors ${
                  isSelected ? 'border-primary bg-primary/10' : 'border-border bg-background hover:border-primary/40'
                }`}
              >
                {isSelected && (
                  <Badge className="absolute -top-2 -right-2 z-10 size-5 rounded-full p-0">
                    <Check className="size-3" />
                  </Badge>
                )}
                {src ? (
                  isHtmlAsset(asset.mimeType ?? '') ? (
                    <ScaledHtmlFrame
                      src={src}
                      width={asset.width || DEFAULT_HTML_SIZE.width}
                      height={asset.height || DEFAULT_HTML_SIZE.height}
                      maxWidth={180}
                      maxHeight={120}
                      interactive={false}
                    />
                  ) : isVideo ? (
                    <video src={src} style={thumbStyle} muted playsInline preload="metadata" loop
                      onMouseEnter={(e) => (e.currentTarget as HTMLVideoElement).play()}
                      onMouseLeave={(e) => { const v = e.currentTarget as HTMLVideoElement; v.pause(); v.currentTime = 0 }}
                    />
                  ) : (
                    <img src={src} alt={asset.fileName} style={thumbStyle} />
                  )
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 120, height: 80 }}>
                    <ImageIcon style={{ width: 32, height: 32, opacity: 0.4 }} />
                  </span>
                )}
              </button>
              </AssetHoverPreview>
            )
          })}

          {/* Uploading placeholder thumbnail — hide once the real asset is in the list */}
          {showUpload && !assets.some((a) => a.id === lastUploadedAsset?.id) && (
            <div
              style={{
                width: 120,
                height: 78,
                borderRadius: 6,
                border: '2px solid var(--ad-border)',
                background: 'var(--ad-bg)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {lastUploadedAsset ? (
                (() => {
                  const isVideo = isVideoAsset(lastUploadedAsset.mimeType ?? '')
                  const src = isVideo ? lastUploadedAsset.cdnId : (lastUploadedAsset.previewCdnId || lastUploadedAsset.cdnId)
                  const style = { width: '100%', height: '100%', objectFit: 'contain' as const, display: 'block', animation: 'ad-fade-in 0.4s ease' }
                  return isVideo
                    ? <video src={src} style={style} muted playsInline preload="metadata" />
                    : <img src={src} alt={lastUploadedAsset.fileName} style={style} />
                })()
              ) : (
                <div style={{ width: '100%', padding: '0 10px' }}>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      background: 'var(--ad-border)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${uploadProgress}%`,
                        background: 'var(--ad-primary)',
                        borderRadius: 2,
                        transition: 'width 0.2s ease',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Inner form ───────────────────────────────────────────────────────────────

interface CreativeEditFormProps {
  creative: Creative | null
  id: string
  isNew: boolean
  lineItemId: string | null
  /** Owner of a new creative made outside a line item. */
  advertiserId?: string | null
}

/** The ad server warns and stops serving a creative with more trackers than this. */
const MAX_IMPRESSION_TRACKING_URLS = 3

function CreativeEditForm({ creative, id, isNew, lineItemId, advertiserId }: CreativeEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()

  // Selected asset ID per layout tag
  const [selectedAssetPerTag, setSelectedAssetPerTag] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    if (!creative?.layoutParameters) return init
    for (const [tag, val] of Object.entries(creative.layoutParameters)) {
      if (val && typeof val === 'object') {
        const assetId = (val as Record<string, string>).id
        if (assetId) init[tag] = assetId
      }
    }
    return init
  })
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [lastUploadedAsset, setLastUploadedAsset] = useState<Asset | null>(null)
  const assetsDirty = useRef(false)
  // True once the creative exists in the backend (false only for new creatives before first save)
  const creativeSaved = useRef(!isNew)

  // Selected layout id
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>(creative?.layoutId ?? '')

  // Given a line item the server narrows the layouts to its product's.
  const layoutLineItemId = creative?.lineItemId || lineItemId || undefined
  const marketplace = !!currentNetwork?.marketplacePlatform
  const { data: availableLayouts = [], isLoading: layoutsLoading } = useQuery({
    queryKey: ['layouts', 'for-creatives', layoutLineItemId, marketplace],
    queryFn: () =>
      LayoutsApi.getForCreatives({ lineItemId: layoutLineItemId, onlyOnAxProducts: marketplace }),
  })

  const hasToken = AuthService.isAuthenticated()

  const { data: existingAssets = [] } = useQuery({
    queryKey: ['creative-assets', id],
    queryFn: () => AssetsApi.getByCreative(id),
    enabled: !isNew && hasToken,
  })

  const { data: selectedLayout } = useQuery<Layout>({
    queryKey: ['layout', selectedLayoutId],
    queryFn: () => LayoutsApi.getById(selectedLayoutId),
    enabled: !!selectedLayoutId && hasToken,
  })

  // Keep the creative's current layout selectable even if its product no longer offers it.
  const layouts = useMemo(
    () =>
      selectedLayout && !availableLayouts.some((l) => l.id === selectedLayout.id)
        ? [selectedLayout, ...availableLayouts]
        : availableLayouts,
    [availableLayouts, selectedLayout]
  )

  const [advancedOpen, setAdvancedOpen] = useState(false)
  // Set once the field is opened from its button, so its first row takes focus.
  const [trackersAdded, setTrackersAdded] = useState(false)
  const impressionTrackingMacros = [
    {
      macro: '[timestamp]',
      label: t('creatives.trackingMacros.timestamp'),
      description: t('creatives.trackingMacros.timestampHelp'),
    },
    {
      macro: '[source_url]',
      label: t('creatives.trackingMacros.sourceUrl'),
      description: t('creatives.trackingMacros.sourceUrlHelp'),
    },
    {
      macro: '[source_url_enc]',
      label: t('creatives.trackingMacros.sourceUrlEnc'),
      description: t('creatives.trackingMacros.sourceUrlEncHelp'),
    },
  ]
  const [zipping, setZipping] = useState(false)

  const handleDownloadAsset = async (asset: Asset) => {
    try {
      await downloadAsset(asset)
    } catch (e) {
      toast.error(t('creatives.downloadAssetFailed'), { description: (e as Error).message })
    }
  }

  // Third-party placeholders only mean something in a third-party ad tag component
  const hasThirdPartyComponent = !!selectedLayout?.layoutComponents.some(
    (c) => c.type === 'TEXT' && c.tag === 'thirdParty'
  )
  const codeVariableGroups = hasThirdPartyComponent
    ? [...THIRD_PARTY_VARIABLE_GROUPS, GENERAL_VARIABLE_GROUP]
    : [GENERAL_VARIABLE_GROUP]

  // Extract initial text/url/choice param values from creative
  const [paramValues, setParamValues] = useState<Record<string, string>>(() => {
    const vals: Record<string, string> = {}
    if (!creative?.layoutParameters) return vals
    for (const [tag, val] of Object.entries(creative.layoutParameters)) {
      if (typeof val === 'string') {
        vals[tag] = val
      }
    }
    return vals
  })

  function setParam(tag: string, value: string) {
    setParamValues((prev) => ({ ...prev, [tag]: value }))
    assetsDirty.current = true
  }

  // Param values are kept per tag across layout switches, so a choice carried
  // over from another layout only counts if this layout offers it. Untouched
  // text and empty choices fall back to the component's default.
  function paramValue(comp: LayoutComponent): string | undefined {
    const v = paramValues[comp.tag]
    if (comp.type === 'CHOICE') {
      if (v && comp.options?.includes(v)) return v
      return comp.defaultValue && comp.options?.includes(comp.defaultValue) ? comp.defaultValue : undefined
    }
    if (comp.type === 'TEXT' && v === undefined) return comp.defaultValue
    return v
  }

  // Asset picks are remembered per layout until save, so switching back restores them.
  const assetsPerLayout = useRef<Record<string, Record<string, string>>>({})

  function switchLayout(layoutId: string) {
    assetsPerLayout.current[selectedLayoutId] = selectedAssetPerTag
    setSelectedLayoutId(layoutId)
    setSelectedAssetPerTag(assetsPerLayout.current[layoutId] ?? {})
  }

  async function ensureCreativeSaved(): Promise<void> {
    if (creativeSaved.current) return
    const values = form.state.values
    const input: CreativeInput = {
      name: values.name || t('creatives.newCreative'),
      objectState: (creative?.objectState ?? 'ACTIVE') as ObjectState,
      width: values.width ? parseInt(values.width) : undefined,
      height: values.height ? parseInt(values.height) : undefined,
      layout: selectedLayoutId || undefined,
      // A line item already fixes the advertiser; only a loose creative needs one.
      ...(lineItemId ? { lineItem: lineItemId } : advertiserId ? { advertiser: advertiserId } : {}),
    }
    await CreativesApi.update(id, input)
    creativeSaved.current = true
    markClean()
  }

  const form = useForm({
    defaultValues: {
      name: creative?.name ?? '',
      // Ready to serve unless the user says otherwise, as UII defaults it.
      userState: creative?.userState || 'APPROVED',
      width: creative?.width ? String(creative.width) : '',
      height: creative?.height ? String(creative.height) : '',
      targeting: (creative?.targeting ?? {}) as Targeting,
      sponsorshipPercentage: creative?.sponsorshipPercentage?.toString() ?? '',
      impressionTrackingUrls: creative?.impressionTrackingUrls ?? [],
    },
    onSubmit: ({ value }) => {
      mutation.mutate(value)
    },
  })

  const saveObjectState = useSaveObjectState(
    (objectState) => CreativesApi.update(id, { name: creative?.name ?? '', objectState }),
    ['creative', id],
    ['creatives']
  )
  const { isDirty: formIsDirty, markClean, blocker } = useUnsavedChanges(form)

  // The preview renders from what is on screen, so assets resolve to their CDN
  // references rather than the { id } shape the save payload uses.
  const previewParameters = useMemo(() => {
    const params: Record<string, PreviewLayoutParameter> = {}
    for (const comp of selectedLayout?.layoutComponents ?? []) {
      const tag = comp.tag
      if (comp.type === 'ASSET') {
        const asset = existingAssets.find((a) => a.id === selectedAssetPerTag[tag])
        if (asset) {
          params[tag] = {
            cdnId: asset.cdnId,
            previewCdnId: asset.previewCdnId,
            width: asset.width,
            height: asset.height,
          }
        }
      } else {
        const v = paramValue(comp)
        if (v !== undefined && v !== '') params[tag] = v
      }
    }
    return params
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paramValue only reads paramValues
  }, [selectedLayout, existingAssets, selectedAssetPerTag, paramValues])
  /** First URL component with a value — the email tag's click-through. */
  const destinationUrl = useMemo(() => {
    for (const comp of selectedLayout?.layoutComponents ?? []) {
      if (comp.type === 'URL' && paramValues[comp.tag]) return paramValues[comp.tag]
    }
    return ''
  }, [selectedLayout, paramValues])

  /**
   * The email tag renders an image, so the creative has to carry one. Mime
   * types come back as the API's enum names, e.g. `IMAGE_PNG`.
   */
  const hasImageAsset = useMemo(
    () =>
      (selectedLayout?.layoutComponents ?? []).some(
        (comp) =>
          comp.type === 'ASSET' &&
          (comp.mimeTypes ?? []).some((mime) => mime.toUpperCase().startsWith('IMAGE_')) &&
          !!selectedAssetPerTag[comp.tag]
      ),
    [selectedLayout, selectedAssetPerTag]
  )

  /** The VAST tag serves a video, so the creative has to carry one. */
  const hasVideoAsset = useMemo(
    () =>
      (selectedLayout?.layoutComponents ?? []).some(
        (comp) =>
          comp.type === 'ASSET' &&
          (comp.mimeTypes ?? []).some((mime) => mime.toUpperCase().startsWith('VIDEO_')) &&
          !!selectedAssetPerTag[comp.tag]
      ),
    [selectedLayout, selectedAssetPerTag]
  )

  const isDirty = formIsDirty || assetsDirty.current

  const mutation = useMutation({
    mutationFn: async (value: {
      name: string
      userState: string
      width: string
      height: string
      targeting: Targeting
      sponsorshipPercentage: string
      impressionTrackingUrls: string[]
    }) => {
      // Build layoutParameters from uploaded assets + text/url/choice param values
      const layoutParameters: Record<string, string | { id: string } | null> = {}
      if (selectedLayout) {
        for (const comp of selectedLayout.layoutComponents) {
          const tag = comp.tag
          if (comp.type === 'ASSET') {
            const selectedId = selectedAssetPerTag[tag]
            if (selectedId) {
              layoutParameters[tag] = { id: selectedId }
            }
          } else {
            const v = paramValue(comp)
            if (v !== undefined) layoutParameters[tag] = v
          }
        }
      }

      const input: CreativeInput = {
        name: value.name,
        objectState: (creative?.objectState ?? 'ACTIVE') as ObjectState,
        userState: value.userState,
        width: value.width ? parseInt(value.width) : undefined,
        height: value.height ? parseInt(value.height) : undefined,
        layout: selectedLayoutId || undefined,
        layoutParameters: Object.keys(layoutParameters).length > 0 ? layoutParameters : undefined,
        // Sent whole: the server replaces the creative's targeting with what the
        // save carries, so a partial payload would leave stale targets behind.
        targeting: targetingForSave(value.targeting),
        // Empty clears it, handing rotation back to the line item.
        sponsorshipPercentage: value.sponsorshipPercentage ? parseFloat(value.sponsorshipPercentage) : null,
        impressionTrackingUrls: value.impressionTrackingUrls.map((u) => u.trim()).filter(Boolean),
      }
      if (isNew && lineItemId) {
        input.lineItem = lineItemId
      } else if (isNew && advertiserId) {
        input.advertiser = advertiserId
      }
      return CreativesApi.update(id, input)
    },
    onSuccess: (data) => {
      if (posthogConfigured) posthog.capture(isNew ? 'creative_created' : 'creative_updated', {
        has_layout: Boolean(data.layoutId),
        has_line_item: Boolean(data.lineItemId),
      })
      queryClient.setQueryData(['creative', id], data)
      // Keep lineitem creative list in sync so the preview column shows the updated asset
      if (data.lineItemId) {
        queryClient.setQueryData(['lineitem-creatives', data.lineItemId], (old: Creative[] | undefined) => {
          if (!old) return old
          const idx = old.findIndex((c) => c.id === data.id)
          return idx >= 0 ? old.map((c) => (c.id === data.id ? data : c)) : old
        })
        queryClient.invalidateQueries({ queryKey: ['lineitem-preview-assets', data.lineItemId] })
      }
      creativeSaved.current = true
      assetsDirty.current = false
      // The server may move the state on, e.g. an approved creative into review.
      if (data.userState) form.setFieldValue('userState', data.userState)
      markClean()
      if (isNew) {
        toast.success(t('common.saved'), { description: t('creatives.createdSuccess'), duration: 3000 })
        navigate(`/advertising/creatives/${data.id}`, { replace: true })
      } else {
        toast.success(t('common.saved'), { description: t('creatives.savedSuccess'), duration: 3000 })
      }
    },
    onError: (error) => {
      const key = isNew ? 'creatives.createFailed' : 'creatives.saveFailed'
      toast.error(t(key), { description: (error as Error).message, duration: 5000 })
    },
  })

  const ancestors = useLineItemAncestors(creative?.lineItemId || lineItemId, {
    advertiser: { id: creative?.advertiserId, name: creative?.advertiserName },
    lineItem: { id: creative?.lineItemId, name: creative?.lineItemName },
  })
  const crumbs = useObjectCrumbs(
    ancestors,
    creative?.name ?? (isNew ? t('creatives.newCreative') : t('creatives.editTitle'))
  )

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={creative?.validationWarnings} />
        {creative?.userState === 'REJECTED' && <CreativeRejectedAlert reason={creative.rejectedReason} />}
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.creative')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('creatives.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && creative && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={creative!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {!isNew && creative && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={creative.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(creative.createTime)}
                  />
                ),
              },
            ]}
          />
        )}

        {/* Editing on the left; stats and the creative it produces on the right */}
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-4">
            {/* Details card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={IdCard} />
                  {t('common.details')}
                </CardTitle>
                <CardAction>
                  <form.Field name="userState">
                    {(field) => (
                      <CreativeStateSelect
                        value={field.state.value}
                        onChange={(s) => field.handleChange(s)}
                        size="md"
                      />
                    )}
                  </form.Field>
                </CardAction>
              </CardHeader>
              <CardContent>
                <ObjectAncestorsList ancestors={ancestors} className="mb-4 border-b border-border/60 pb-4" />

                <div className="field-grid">
                  <div className="p-field">
                    <label>{t('creatives.width')}</label>
                    <form.Field name="width">
                      {(field) => (
                        <Input
                          type="number"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder="e.g. 300"
                        />
                      )}
                    </form.Field>
                  </div>

                  <div className="p-field">
                    <label>{t('creatives.height')}</label>
                    <form.Field name="height">
                      {(field) => (
                        <Input
                          type="number"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder="e.g. 250"
                        />
                      )}
                    </form.Field>
                  </div>
                </div>

                <form.Subscribe
                  selector={(state) => [state.values.sponsorshipPercentage, state.values.impressionTrackingUrls] as const}
                >
                  {([sov, trackers]) => (
                    <>
                      <AdvancedSection
                        open={advancedOpen}
                        onOpenChange={setAdvancedOpen}
                        activeCount={sov ? 1 : 0}
                        className="mt-4"
                        actions={
                          trackers.length === 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setTrackersAdded(true)
                                form.setFieldValue('impressionTrackingUrls', [''])
                              }}
                            >
                              <Plus className="size-3.5" />
                              {t('creatives.addImpressionTrackingUrl')}
                            </Button>
                          )
                        }
                      >
                        <div className="field-grid">
                        <div className="p-field full">
                          <FieldLabel tooltip={t('creatives.shareOfVoiceHelp')}>
                            {t('creatives.shareOfVoice')}
                          </FieldLabel>
                          <form.Field
                            name="sponsorshipPercentage"
                            validators={{
                              onChange: ({ value }) => {
                                if (!value) return undefined
                                const n = parseFloat(value)
                                return n > 0 && n <= 100 ? undefined : t('creatives.shareOfVoiceInvalid')
                              },
                            }}
                          >
                            {(field) => (
                              <>
                                <ShareOfVoiceInput
                                  value={field.state.value}
                                  onChange={(v) => field.handleChange(v)}
                                  label={t('creatives.shareOfVoice')}
                                  placeholder="—"
                                  optional
                                  summary={(share) =>
                                    share <= 0
                                      ? t('creatives.shareOfVoiceSummary.none')
                                      : share >= 100
                                        ? t('creatives.shareOfVoiceSummary.all')
                                        : t('creatives.shareOfVoiceSummary.split', {
                                            share: formatNumber(share),
                                            rest: formatNumber(100 - share),
                                          })
                                  }
                                />
                                {field.state.meta.errors[0] && (
                                  <p className="text-xs text-destructive">{field.state.meta.errors[0] as string}</p>
                                )}
                              </>
                            )}
                          </form.Field>
                        </div>
                        </div>
                      </AdvancedSection>
                      {trackers.length > 0 && (
                        <div className="field-grid mt-4">
                          <div className="p-field full">
                            <FieldLabel
                              tooltip={t('creatives.impressionTrackingUrlsHelp')}
                              hint={t('creatives.impressionTrackingUrlsLimit', { max: MAX_IMPRESSION_TRACKING_URLS })}
                            >
                              {t('creatives.impressionTrackingUrls')}
                            </FieldLabel>
                            <form.Field name="impressionTrackingUrls">
                              {(field) => (
                                <TrackingUrlListInput
                                  value={field.state.value}
                                  onChange={(v) => field.handleChange(v)}
                                  max={MAX_IMPRESSION_TRACKING_URLS}
                                  addLabel={t('creatives.addImpressionTrackingUrl')}
                                  placeholder="https://"
                                  macros={impressionTrackingMacros}
                                  autoFocus={trackersAdded}
                                />
                              )}
                            </form.Field>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </form.Subscribe>
              </CardContent>
            </Card>

            <form.Field name="targeting">
              {(field) => (
                <TargetingCard
                  targeting={field.state.value}
                  onChange={(targeting) => field.handleChange(targeting)}
                />
              )}
            </form.Field>

            {/* Layout card */}
            <Card tone="creative">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={LayoutList} />
                  {t('creatives.layout')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('creatives.layout')}</label>
                    {layoutsLoading ? (
                      <span className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Loader2 className="size-3.5 animate-spin" />
                        {t('common.loading')}
                      </span>
                    ) : (
                      <Select
                        label={t('creatives.layout')}
                        value={selectedLayoutId}
                        onValueChange={(v) => switchLayout(v ?? '')}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('creatives.selectLayout')}>
                            {layouts.find((l) => l.id === selectedLayoutId)?.name ?? t('creatives.selectLayout')}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {layouts.map((layout) => (
                            <SelectItem key={layout.id} value={layout.id}>
                              {layout.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Dynamic layout parameter fields */}
                  {selectedLayout && selectedLayout.layoutComponents.map((comp) => {
                    const tag = comp.tag
                    const label = comp.name ?? tag

                    if (comp.type === 'ASSET') {
                      return (
                        <AssetSlot
                          key={tag}
                          label={label}
                          assets={existingAssets}
                          selectedAssetId={selectedAssetPerTag[tag] ?? null}
                          mimeTypes={comp.mimeTypes}
                          onSelect={(assetId) => {
                            setSelectedAssetPerTag((prev) => ({ ...prev, [tag]: assetId }))
                            assetsDirty.current = true
                            const asset = existingAssets.find((a) => a.id === assetId)
                            if (asset?.width) form.setFieldValue('width', String(asset.width))
                            if (asset?.height) form.setFieldValue('height', String(asset.height))
                          }}
                          uploadProgress={uploadProgress}
                          lastUploadedAsset={lastUploadedAsset}
                          onDownloadAsset={handleDownloadAsset}
                          downloadLabel={t('common.download')}
                        />
                      )
                    }

                    if (comp.type === 'TEXT') {
                      const isMultiline = comp.textType === 'MULTILINE'
                      const isThirdParty = tag === 'thirdParty'
                      const isCode = comp.textType === 'CODE' || isThirdParty
                      if (isCode) {
                        return (
                          <div key={tag} className="p-field full">
                            <div className="flex items-center justify-between gap-2">
                              <label>{label}</label>
                              <VariableReferenceSheet groups={codeVariableGroups} />
                            </div>
                            <CodeEditor
                              value={paramValue(comp) ?? ''}
                              onChange={(value) => setParam(tag, value)}
                              variables={isThirdParty ? 'thirdPartyCode' : 'generalVariables'}
                              minHeight="300px"
                              maxHeight="480px"
                              placeholder={label}
                            />
                          </div>
                        )
                      }
                      return (
                        <div key={tag} className="p-field full">
                          <label>{label}</label>
                          {isMultiline ? (
                            <Textarea
                              value={paramValue(comp) ?? ''}
                              onChange={(e) => setParam(tag, e.target.value)}
                              placeholder={label}
                              rows={3}
                            />
                          ) : (
                            <Input
                              value={paramValue(comp) ?? ''}
                              onChange={(e) => setParam(tag, e.target.value)}
                              placeholder={label}
                            />
                          )}
                        </div>
                      )
                    }

                    if (comp.type === 'URL') {
                      return (
                        <div key={tag} className="p-field full">
                          <label>{label}</label>
                          <Input
                            type="url"
                            value={paramValues[tag] ?? ''}
                            onChange={(e) => setParam(tag, e.target.value)}
                            placeholder="https://"
                          />
                        </div>
                      )
                    }

                    if (comp.type === 'CHOICE' && comp.options?.length) {
                      return (
                        <div key={tag} className="p-field">
                          <label>{label}</label>
                          <Select
                            label={label}
                            value={paramValue(comp) ?? ''}
                            onValueChange={(v) => setParam(tag, v ?? '')}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={label}>
                                {paramValue(comp) ?? label}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {comp.options.map((opt) => (
                                <SelectItem key={opt} value={opt}>
                                  {opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )
                    }

                    return null
                  })}

                  {!selectedLayoutId && (
                    <div className="p-field full">
                      <p className="text-sm text-muted-foreground">{t('creatives.noLayout')}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Assets card — only when layout has asset slots */}
            {selectedLayout?.layoutComponents.some((c) => c.type === 'ASSET') && (
              <Card tone="creative">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Upload} />
                    {t('creatives.assets')}
                  </CardTitle>
                  {existingAssets.length > 0 && (
                    <CardAction>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        loading={zipping}
                        onClick={async () => {
                          setZipping(true)
                          try {
                            const count = await downloadAssetsZip(
                              existingAssets,
                              form.getFieldValue('name') || creative?.name || id
                            )
                            toast.success(t('creatives.downloadAssetsDone', { count }))
                          } catch (e) {
                            toast.error(t('creatives.downloadAssetFailed'), {
                              description: (e as Error).message,
                            })
                          } finally {
                            setZipping(false)
                          }
                        }}
                      >
                        <Download className="size-3.5" />
                        {t('creatives.downloadAllAssets')}
                      </Button>
                    </CardAction>
                  )}
                </CardHeader>
                <CardContent>
                  <AssetDropzone
                    isUploading={isUploading}
                    onUpload={async (file) => {
                      setIsUploading(true)
                      setUploadProgress(0)
                      setLastUploadedAsset(null)
                      try {
                        await ensureCreativeSaved()
                        const asset = await AssetsApi.uploadForCreativeWithProgress(id, file, (pct) => {
                          setUploadProgress(pct)
                        })
                        setLastUploadedAsset(asset)
                        setUploadProgress(null)
                        if (asset.width) form.setFieldValue('width', String(asset.width))
                        if (asset.height) form.setFieldValue('height', String(asset.height))
                        // Straight into the list: a creative first saved by this
                        // upload is still "new", and its asset query never runs.
                        queryClient.setQueryData<Asset[]>(['creative-assets', id], (old = []) =>
                          old.some((a) => a.id === asset.id) ? old : [...old, asset]
                        )
                        await queryClient.invalidateQueries({ queryKey: ['creative-assets', id] })
                        await queryClient.invalidateQueries({ queryKey: ['creative-assets-preview', id] })
                        // The upload goes to the first component that takes it —
                        // an empty one if there is one, otherwise replacing the
                        // asset in the first that fits.
                        const fits = selectedLayout?.layoutComponents.filter(
                          (c) => c.type === 'ASSET' && (!c.mimeTypes?.length || c.mimeTypes.includes(asset.mimeType))
                        ) ?? []
                        const target = fits.find((c) => !selectedAssetPerTag[c.tag]) ?? fits[0]
                        if (target) {
                          setSelectedAssetPerTag((prev) => ({ ...prev, [target.tag]: asset.id }))
                        }
                        assetsDirty.current = true
                      } catch (e) {
                        toast.error(t('creatives.uploadFailed'), { description: (e as Error).message, duration: 5000 })
                        setUploadProgress(null)
                      } finally {
                        setIsUploading(false)
                      }
                    }}
                    uploadLabel={t('creatives.uploadAsset')}
                    uploadingLabel={t('creatives.assetUploading')}
                  />

                </CardContent>
              </Card>
            )}

          </div>

          <div className="flex min-w-0 flex-col gap-4 self-stretch">
            {!isNew && creative && (
              <div className="ad-panel">
                <RouteTabs
                  tabs={[
                    {
                      key: 'statistics',
                      label: t('creatives.statistics'),
                      icon: <ChartLine />,
                      content: () => (
                        <StatsPanel
                          query={{ creativeId: creative.id, groupBy: 'HALF_HOURLY' }}
                          storageKey={`creative-stats-${creative.id}`}
                          variant="advertising"
                          auditObject={{ type: 'creative', id: creative.id }}
                        />
                      ),
                    },
                    {
                      key: 'tags',
                      label: t('creativeTags.title'),
                      icon: <Code2 />,
                      content: () => (
                        <CreativeTagsPanel
                          creative={{
                            id: creative.id,
                            name: creative.name,
                            width: creative.width,
                            height: creative.height,
                          }}
                          lineItemId={creative.lineItemId}
                          networkTagId={currentNetwork?.tagId ?? ''}
                          destinationUrl={destinationUrl}
                          hasImage={hasImageAsset}
                          hasVideo={hasVideoAsset}
                        />
                      ),
                    },
                  ]}
                />
              </div>
            )}

            {/* Live preview of the creative as it is being edited — follows the scroll */}
            {selectedLayoutId && (
              <div className="lg:sticky lg:top-4">
                <form.Subscribe selector={(state) => [state.values.width, state.values.height] as const}>
                  {([w, h]) => (
                    <CreativePreviewCard
                      layoutId={selectedLayoutId}
                      width={parseInt(w) || parseInt(h) || 300}
                      height={parseInt(h) || parseInt(w) || 300}
                      layoutParameters={previewParameters}
                      lineItemId={creative?.lineItemId || lineItemId || undefined}
                      creativeId={!isNew ? id : undefined}
                      creativeName={creative?.name}
                    />
                  )}
                </form.Subscribe>
              </div>
            )}
          </div>
        </div>

        {/* Save bar */}
        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/advertising/creatives')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              <Check className="size-4" />
              {t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ───────────────────────────────────────────────────────────────

export function CreativeEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const hasToken = AuthService.isAuthenticated()

  const isNew = searchParams.get('new') === 'true'
  const lineItemId = searchParams.get('lineitem')
  const advertiserId = searchParams.get('advertiser')

  const { data: creative, isLoading, isError } = useQuery({
    queryKey: ['creative', id],
    queryFn: () => CreativesApi.getById(id!),
    enabled: !!id && hasToken && !isNew,
    retry: false,
  })

  const ancestors = useLineItemAncestors(creative?.lineItemId || lineItemId, {
    advertiser: { id: creative?.advertiserId, name: creative?.advertiserName },
    lineItem: { id: creative?.lineItemId, name: creative?.lineItemName },
  })
  const crumbs = useObjectCrumbs(
    ancestors,
    creative?.name ?? (isNew ? t('creatives.newCreative') : t('creatives.editTitle'))
  )

  if (isError) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (!isNew && (isLoading || !creative)) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <CreativeEditForm
      key={id}
      creative={creative ?? null}
      id={id!}
      isNew={isNew}
      lineItemId={lineItemId}
      advertiserId={advertiserId}
    />
  )
}
